export const DOWNLOAD_SECONDS = 3 * 60 * 60;
export const DOWNLOAD_CLOCK_SKEW_SECONDS = 60;
export const DOWNLOAD_TOKEN = /^[a-f0-9]{64}$/;
const pathPattern = /^\/_shop-download\/([A-Za-z][A-Za-z0-9_-]{0,63})\/([a-f0-9]{64})\/([A-Za-z][A-Za-z0-9_-]{0,63})\/([1-9][0-9]{0,9})$/;
const encoder = new TextEncoder();
async function key(secret) {
    if (secret.length < 32)
        throw new Error("Download signing secret is not configured");
    return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}
const message = (path, expires) => encoder.encode(`shop-download-v1\n${path}\n${expires}`);
export async function signedDownload(origin, siteId, deliveryId, fileId, version, secret, now = Date.now()) {
    const url = new URL(`/_shop-download/${siteId}/${deliveryId}/${fileId}/${version}`, origin);
    if (!pathPattern.test(url.pathname))
        throw new Error("Invalid download reference");
    const expires = String(Math.floor(now / 1000) + DOWNLOAD_SECONDS);
    const signature = await crypto.subtle.sign("HMAC", await key(secret), message(url.pathname, expires));
    url.searchParams.set("expires", expires);
    url.searchParams.set("signature", Array.from(new Uint8Array(signature), (b) => b.toString(16).padStart(2, "0")).join(""));
    return url.href;
}
export async function verifyDownload(url, secret, now = Date.now()) {
    const match = pathPattern.exec(url.pathname);
    const expires = url.searchParams.get("expires") ?? "";
    const signature = url.searchParams.get("signature") ?? "";
    const time = Math.floor(now / 1000);
    if (!match || !secret || secret.length < 32 || !/^[0-9]{1,12}$/.test(expires) || !/^[a-f0-9]{64}$/.test(signature)
        || Number(expires) + DOWNLOAD_CLOCK_SKEW_SECONDS <= time || Number(expires) > time + DOWNLOAD_SECONDS + DOWNLOAD_CLOCK_SKEW_SECONDS
        || url.searchParams.getAll("expires").length !== 1 || url.searchParams.getAll("signature").length !== 1)
        return null;
    const bytes = Uint8Array.from(signature.match(/../g), (s) => parseInt(s, 16));
    if (!await crypto.subtle.verify("HMAC", await key(secret), bytes, message(url.pathname, expires)))
        return null;
    return { siteId: match[1], deliveryId: match[2], fileId: match[3], version: Number(match[4]) };
}
/** One byte range, normalized against the immutable size; invalid or multiple ranges are ignored; valid unsatisfiable ranges return false. */
export function downloadRange(header, size) {
    if (header === null)
        return null;
    const match = /^bytes=(\d*)-(\d*)$/.exec(header);
    if (!match || (!match[1] && !match[2]))
        return null;
    const a = match[1] ? Number(match[1]) : null;
    const b = match[2] ? Number(match[2]) : null;
    if ((a !== null && !Number.isSafeInteger(a)) || (b !== null && !Number.isSafeInteger(b)) || (a !== null && b !== null && b < a))
        return null;
    if (a !== null && a >= size)
        return false;
    if (a === null)
        return b > 0 ? { offset: Math.max(0, size - b), length: Math.min(size, b) } : false;
    const end = b === null ? size - 1 : Math.min(size - 1, b);
    return end >= a ? { offset: a, length: end - a + 1 } : false;
}
export function attachmentFilename(name) {
    const safe = Array.from(name, (char) => {
        const code = char.codePointAt(0);
        return code < 32 || code === 127 || (code >= 0xd800 && code <= 0xdfff) || char === "/" || char === "\\" ? "_" : char;
    }).join("");
    const fallback = safe.replace(/[^\x20-\x7e]|["\\]/g, "_") || "download";
    const encoded = encodeURIComponent(safe || "download").replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
    return `attachment; filename="${fallback}"; filename*=UTF-8''${encoded}`;
}
